import React from 'react';
import { motion } from 'framer-motion';
import { RiInformationLine } from 'react-icons/ri';
import CategoryGrid from '../components/FloraFauna/CategoryGrid';
import EndemismSection from '../components/FloraFauna/EndemismSection';
import ConservationStatusChart from '../components/FloraFauna/ConservationStatusChart';
import { categories, endemismStats, iucnCategories } from '../constants/floraFaunaData';
import Footer from '../components/Footer';

const FloraFauna = () => {
    return (
        <>
            <section className="py-24 relative overflow-hidden bg-white " id="living-archive">
<br/><br/>
                {/* Technical Grid Background */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(#0f172a 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="max-w-4xl mx-auto text-center mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6 sm:mb-8 font-display leading-[1.2] sm:leading-[1.1]">
                            Biodiversity <span className="text-emerald-600">Inventory.</span>
                        </h2>

                        <p className="text-base sm:text-lg text-slate-500 font-medium leading-relaxed max-w-2xl mx-auto italic font-sans">
                            A high-fidelity mapping of global and regional biological richness, organized by scientific classification.
                        </p>
                    </motion.div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    {/* Taxonomic Category Grid */}
                    <CategoryGrid categories={categories} />

                    {/* Endemism Statistics Highlights */}
                    <EndemismSection stats={endemismStats} />

                    {/* Conservation Status Chart */}
                    <ConservationStatusChart iucnCategories={iucnCategories} />
                </div>
            </section>

            <div className="bg-white pt-32">
                <div className="container mx-auto px-6 max-w-7xl pb-32">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl font-bold text-slate-900 mb-8 font-display">
                                Contributing <span className="text-emerald-600">to the Global</span> Database
                            </h2>
                            <p className="text-slate-500 mb-8 leading-relaxed font-sans">
                                Our research team works in collaboration with international scientific bodies to maintain this living archive. Every record is verified through field investigation and peer review to ensure the highest data integrity.
                            </p>

                            <div className="space-y-6">
                                {[
                                    "Real-time population tracking in critical habitats.",
                                    "Extensive genomic archiving for endemic species.",
                                    "Cross-border database integration with environmental agencies."
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4 items-center">
                                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                                            <div className="w-2 h-2 bg-emerald-600 rounded-full" />
                                        </div>
                                        <span className="text-sm font-bold text-slate-700 font-sans">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="bg-slate-900 p-12 rounded-[40px] text-white relative overflow-hidden"
                        >
                            <div className="relative z-10">
                                <RiInformationLine size={40} className="text-emerald-400 mb-8" />
                                <h3 className="text-2xl font-bold mb-6">Need specific scientific data?</h3>
                                <p className="text-white/70 mb-10 leading-relaxed">
                                    Our full archives are available to academic institutions and certified research organizations upon request. Contact our data center for specialized datasets.
                                </p>
                                <button className="px-8 py-4 bg-white text-slate-900 font-black uppercase tracking-widest text-xs rounded-2xl hover:bg-emerald-400 transition-all font-display">
                                    Request Data Access
                                </button>
                            </div>

                            {/* Decorative background elements */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        </motion.div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default FloraFauna;
